import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import { startEtaLoad } from "../../redux/actions/EtaActions";
import { startConfigSave } from "../../redux/actions/SaveActions";
import type { BuildAndBuyDispatchType } from "../../redux/store";
import { useBnbSelector } from "../../redux/store";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import { showBnbInsurance, showBnbMonthlyRates } from "../../utils/settings/bnbFinanceSettingUtils";
import { getLocalStorageParams, removeUnfinishedConfiguration } from "../../utils/localStorage";
import { configureCtxConfig } from "../../../../shared-logic/utils/saveUtils";
import { invalidateSpecs } from "../../redux/actions/SpecActions";
import { getSessionStorageParams, getTaxLocationIdFromStorage } from "../../utils/sessionStorage";
import { setExternalPriceTaxLocation } from "../../redux/actions/ExternalPriceActions";
import { enableTaxAndCostsSelector } from "../../utils/selectorUtils";
import { DEFAULT_TAX_LOCATION } from "../../../../../common-deprecated/utils/taxLocations";
import { useDispatch } from "../../../../../shared-logic/redux/store";

// This hook is used to initialize data used in the summary, no longer checks if step is correct as it should be only used on summary views
const useInitSummary = (): void => {
    const car = useBnbSelector((state) => state.car);
    const save = useBnbSelector((state) => state.save);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const step = useBnbSelector((state) => state.navigation.step);
    const etaLoaded = useBnbSelector((state) => state.eta.loaded);
    const modelCode = useBnbSelector((state) => state.car.model.internalCode);
    const modelId = useBnbSelector((state) => state.car.model.id);
    const enableTaxAndCosts = useBnbSelector(enableTaxAndCostsSelector, shallowEqual);
    const specs = useBnbSelector((state) => state.specs);

    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    useEffect(() => {
        if (step === NavStep.Summary) {
            // We always need to refetch the summary data on v2, since the summary component and thus this hook is unmounted when closing the modal
            const loadFinanceRates = showBnbMonthlyRates(commonSettings, bnbSettings, modelCode, step, car);
            const loadInsuranceRates = showBnbInsurance(commonSettings, bnbSettings, car);

            const sessionStorageParams = getSessionStorageParams(commonSettings);

            // SessionStorage will contain the taxLocationId if it has been manually set by the user.
            // If we don't already have a location ID (query string/tyCode/...) use that one.
            const sessionTaxLocationId = getTaxLocationIdFromStorage(sessionStorageParams);
            // Default to "75" as this is the value of Paris location
            if (enableTaxAndCosts)
                dispatch(setExternalPriceTaxLocation(sessionTaxLocationId || DEFAULT_TAX_LOCATION, false));

            // Remove the unfinished configuration from localstorage on summary as this means it has been finished.
            removeUnfinishedConfiguration(getLocalStorageParams(commonSettings), modelId);

            // Only save the configuration when it's not saved yet and no finance quotes are available on v1,
            // if there are finance quotes available it will get saved upon initial setting of the finance quote id's
            let shouldSaveConfig = false;
            if (!(loadFinanceRates || loadInsuranceRates)) {
                shouldSaveConfig = true;
            }

            if (shouldSaveConfig && !save.saved && !save.saving) {
                dispatch(startConfigSave());
                // Make sure that the T1.ctx.config object is initialized
            } else if (save.data) {
                // This logic is for handling the CTA's, required for the logic of GFI:
                configureCtxConfig(save.data);
            }

            // Eta gets invalidated on v2 when configuration changes,
            // this means we can use the loaded prop check here even when summary has already been opened once
            if (bnbSettings.showETA && !etaLoaded) dispatch(startEtaLoad());

            if (!specs.fetched) dispatch(invalidateSpecs());
        }
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useInitSummary;
